import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@/components/layout/AppShell';
import { PageContainer } from '@/components/layout/PageContainer';
import { TopBar } from '@/components/layout/TopBar';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { BottomSheet } from '@/components/ui/BottomSheet';
import { CountryPicker } from '@/components/domain/CountryPicker';
import { useCreateCompany } from '@/api/queries/employer';
import { paths } from '@/routes/paths';
import { useUIStore } from '@/store/ui';
import { isAPIError } from '@/api/error';

export function BusinessCreatePage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const create = useCreateCompany();
  const pushToast = useUIStore((s) => s.pushToast);

  const [legalName, setLegalName] = useState('');
  const [tradingName, setTradingName] = useState('');
  const [country, setCountry] = useState('');
  const [countryName, setCountryName] = useState('');
  const [taxId, setTaxId] = useState('');
  const [website, setWebsite] = useState('');
  const [hqCity, setHqCity] = useState('');
  const [picker, setPicker] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!legalName || !country) {
      pushToast({ body: t('errors.validation'), variant: 'error' });
      return;
    }
    try {
      await create.mutateAsync({
        legal_name: legalName,
        trading_name: tradingName || undefined,
        country_code: country,
        tax_id: taxId || undefined,
        website: website || undefined,
        hq_city: hqCity || undefined,
      });
      pushToast({ body: t('business.create_form.pending_msg'), variant: 'success' });
      navigate(paths.business);
    } catch (err) {
      const key = isAPIError(err) ? err.i18nKey : 'errors.generic';
      pushToast({ body: t(key, { defaultValue: t('errors.generic') }), variant: 'error' });
    }
  };

  return (
    <AppShell>
      <PageContainer>
        <TopBar back title={t('business.create_form.title')} />
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Input label={t('business.create_form.legal_name')} value={legalName} onChange={(e) => setLegalName(e.target.value)} />
          <Input label={t('business.create_form.trading_name')} value={tradingName} onChange={(e) => setTradingName(e.target.value)} />
          <Input label={t('business.create_form.country')} value={countryName} readOnly onClick={() => setPicker(true)} />
          <Input label={t('business.create_form.tax_id')} value={taxId} onChange={(e) => setTaxId(e.target.value)} />
          <Input label={t('business.create_form.website')} type="url" value={website} onChange={(e) => setWebsite(e.target.value)} />
          <Input label={t('business.create_form.hq_city')} value={hqCity} onChange={(e) => setHqCity(e.target.value)} />
          <Button type="submit" block size="lg" variant="primary" loading={create.isPending}>
            {t('business.create_form.submit')}
          </Button>
        </form>

        <BottomSheet open={picker} onClose={() => setPicker(false)} title={t('business.create_form.country')}>
          <CountryPicker
            value={country}
            onPick={(code, name) => {
              setCountry(code);
              setCountryName(name);
              setPicker(false);
            }}
          />
        </BottomSheet>
      </PageContainer>
    </AppShell>
  );
}
